import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import WallTabs from './components/WallTabs';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import { useGetMenus, useCreateMenu, useDeleteMenu } from '../../../queries/signageMenus';

const CURRENCIES = ['SAR', 'AED', 'KWD', 'BHD', 'QAR', 'OMR', 'JOD', 'EGP', 'IQD', 'LBP', 'USD', 'EUR'];

/**
 * Smart Template Engine — Menus library. Bilingual restaurant menus the signage
 * templates render. List + quick-create (name + currency) → full editor.
 * (.claude/TEMPLATE_ENGINE_SPEC.md)
 */
export default function VideoWallMenus() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: menus = [], isLoading } = useGetMenus();
  const create = useCreateMenu();
  const del = useDeleteMenu();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('SAR');

  const openEditor = (id) => navigate(`/apps/video-wall/menus/${id}`);
  const openCreate = () => { setName(''); setCurrency('SAR'); setCreating(true); };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = await create.mutateAsync({ name: trimmed, currency });
    const body = res?.data?.data ?? res?.data;
    setCreating(false);
    const id = body?.menu?.id;
    if (id) openEditor(id);
  };

  const confirmDelete = (m) => confirmDialog({
    message: t('video_wall.menus.delete_confirm', { name: m.name }),
    header: t('video_wall.menus.delete_title'),
    icon: 'pi pi-exclamation-triangle',
    acceptClassName: 'p-button-danger',
    accept: () => del.mutate(m.id),
  });

  return (
    <Layout>
      <ConfirmDialog />
      <WallTabs />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          {!isRestaurant && (
          <Button text icon="pi pi-arrow-left" label={t('video_wall.library.back_to_walls')}
            onClick={() => navigate('/apps/video-wall/walls')} className="mb-2 p-0" />
          )}
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-list me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.menus.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.menus.subtitle')}
          </p>
        </div>
        <Button label={t('video_wall.menus.create')} icon="pi pi-plus" onClick={openCreate} />
      </div>

      {isLoading ? (
        <div className="row g-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="col-12 col-md-6 col-xl-4"><Skeleton height="7rem" /></div>
          ))}
        </div>
      ) : menus.length === 0 ? (
        <div className="pp-card p-5 text-center">
          <span className="pp-empty-icon mb-3" style={{ width: 56, height: 56, fontSize: '1.5rem' }}>
            <i className="pi pi-list" />
          </span>
          <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menus.empty_title')}</h5>
          <p className="text-muted">{t('video_wall.menus.empty_subtitle')}</p>
          <Button label={t('video_wall.menus.create')} icon="pi pi-plus" onClick={openCreate} />
        </div>
      ) : (
        <div className="row g-3">
          {menus.map((m) => (
            <div key={m.id} className="col-12 col-md-6 col-xl-4">
              <div className="pp-card pp-card-clickable p-4 h-100 d-flex flex-column"
                role="button" tabIndex={0}
                onClick={() => openEditor(m.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(m.id); } }}>
                <div className="flex-grow-1">
                  <h5 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>{m.name}</h5>
                  <span className="badge" style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)' }}>
                    {m.currency || 'SAR'} · {t('video_wall.menus.sections_count', { n: m.sections_count ?? 0 })}
                  </span>
                </div>
                <div className="pp-card-actions mt-3">
                  <Button size="small" icon="pi pi-pencil" label={t('video_wall.menus.edit')}
                    onClick={(e) => { e.stopPropagation(); openEditor(m.id); }} />
                  <Button className="pp-card-del" text rounded severity="danger" size="small" icon="pi pi-trash"
                    onClick={(e) => { e.stopPropagation(); confirmDelete(m); }} aria-label={t('video_wall.menus.delete')} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog visible={creating} onHide={() => setCreating(false)} header={t('video_wall.menus.create_title')}
        style={{ width: 'min(94vw, 460px)' }}
        footer={(
          <div className="d-flex justify-content-end gap-2">
            <Button label={t('video_wall.library.cancel')} text onClick={() => setCreating(false)} />
            <Button label={t('video_wall.library.save')} icon="pi pi-check" onClick={save}
              loading={create.isLoading} disabled={!name.trim()} />
          </div>
        )}>
        <div className="d-flex flex-column gap-3 pt-2">
          <div>
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.menus.name')}
            </label>
            <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('video_wall.menus.name_ph')} maxLength={150} autoFocus />
          </div>
          <div>
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.menus.currency')}
            </label>
            <Dropdown className="w-100" value={currency} options={CURRENCIES.map((c) => ({ label: c, value: c }))}
              onChange={(e) => setCurrency(e.value)} />
          </div>
        </div>
      </Dialog>
    </Layout>
  );
}
